import React, { type JSX } from 'react';

import {
    widgetList,
    setDashboardId,
    resetDashboard,
    setWidgetList,
    configureSummaryValues,
    removeSummaryWidgetLoader,
    mqttTopics,
} from '@fiji/common/src/features/dashboardManagement/commonDashboardSlice';

import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { CustomTransComponent, DynamicWidget, FailedLoadTriggerModal, Loader } from '../../components';
import { Stack, Box, Button, Card, CardContent, CardHeader, Divider, Typography } from '@mui/material';
import RGL, { WidthProvider } from 'react-grid-layout';
import { resetLocalDashboard } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { CreateDashboardModal } from '../DashboardManagement/CreateDashboard';
import Widgets from '@mui/icons-material/Widgets';
import DesignServices from '@mui/icons-material/DesignServices';
import { useGetActiveDashboardsQuery } from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { useCommonWidgetConfiguration, useDashboardCommonHandlers, useGetWidgetDetails } from '@fiji/common/src/hooks';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { DetailsCardSkeleton } from 'pages/WidgetManagement/common/Skeletons';
import { useRBAC } from 'hooks';
import { useTheme } from '@mui/material/styles';

import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import {
    customEndpointSelector,
    systemTimezone,
    timeStampHandler,
    getWidgetTypeValidationConfig,
    isWidgetApplicable,
    isWidgetConfigured,
} from '@fiji/common/src/utils/helpers';
import { GRID_COLS, GRID_ROWS } from '@fiji/common/src/constants';
import { useParams } from 'react-router-dom';
import { api } from '@fiji/common/src/app/api/baseApi';
import { ScoreModal } from 'pages/WidgetManagement/common/ScoreModal';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { downloadFile, downloadFileFromLink, getUTCTimestamp } from 'utils/helpers';
import { useTranslation } from 'react-i18next';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { resetTrendsLegendData } from '@fiji/common/src/features/widgetManagement/trendsWidgetSlice';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import { UserProfile } from '@fiji/common/src/types';
import { subscribeTopic, unsubscribeTopic } from '@fiji/common/src/mqtt_connection/mqttConnection';
import { CHART_TYPE } from 'pages/WidgetManagement/common';
import { setFailedLoadTrigger } from '@fiji/common/src/features/loadGroups/loadGroupsSlice';
import { useUpdateLoadGroupsMutation } from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import type { GroupState } from 'components/CustomSwitchBtn/CustomSwitchBtn';
import { useUpdateWidgetMutation } from '@fiji/common/src/features/widgetManagement/widgetApi';
import { SelectLoadGroupsModal } from 'pages/WidgetManagement/Widgets/load_groups/SelectLoadGroupsModal';
import { CreateLoadGroupModal } from 'pages/LoadsGroups/CreateLoadGroupModal';
import { normalizeWidgetGridLayout } from '../DashboardManagement/Common/utils/layout';
const ResponsiveReactGridLayout = WidthProvider(RGL);
const SECONDARY_CARD_GAP_X = 16;
const SECONDARY_CARD_GAP_Y = 16;
const SECONDARY_GRID_MARGIN: [number, number] = [SECONDARY_CARD_GAP_X, SECONDARY_CARD_GAP_Y];
const SECONDARY_MIN_GRID_ROWS = 2;

const VALID_LOAD_GROUP_STATES = new Set(['ENABLED', 'DISABLED', 'PARTIAL', 'PENDING']);

const parseLoadGroupStateString = (value: any): GroupState | null => {
    if (typeof value !== 'string') return null;
    const upper = value.toUpperCase();
    return VALID_LOAD_GROUP_STATES.has(upper) ? (upper as GroupState) : null;
};

const parseBooleanLoadGroupState = (value: any): GroupState | null => {
    if (typeof value !== 'boolean') return null;
    return value ? 'ENABLED' : 'DISABLED';
};

const buildTimeRangePayload = (
    widgetTypeId: string,
    data: any,
    config: any,
    range: { from: any; to: any; prevFrom: any; prevTo: any },
    normalizedTimeFilter: string
): any => {
    const { from, to, prevFrom, prevTo } = range;
    if (widgetTypeId === 'metrics' || widgetTypeId === 'load_trends') {
        return {
            ...data,
            currentState: { startTime: from, endTime: to },
            previousState: { startTime: prevFrom, endTime: prevTo },
            timeFilter: normalizedTimeFilter,
        };
    }
    if (widgetTypeId === 'weather') {
        return { ...data, timeFilter: normalizedTimeFilter };
    }
    const payloadData = { ...data, startTime: from, endTime: to, timeFilter: normalizedTimeFilter };
    if (widgetTypeId === 'aggregated_trends') {
        return { ...payloadData, unit: config?.unitType };
    }
    return payloadData;
};

const isLoadTrendsTimeFilterWidget = (widgetTypeId: string): boolean =>
    widgetTypeId === 'load_trends' || widgetTypeId === 'weather' || widgetTypeId === 'aggregated_trends';

const getTrendsPayloadKey = (widgetType: string): string =>
    widgetType === 'load_group_trends' ? 'load_group_trends-getLoadGroupTrends' : 'trends';

const buildTrendsExportRange = (
    isLoadGroup: boolean,
    selectedPayload: any,
    widgetPayload: any
): { from: any; to: any } =>
    isLoadGroup
        ? { from: selectedPayload?.activeDuration?.from, to: selectedPayload?.activeDuration?.to }
        : { from: getUTCTimestamp(widgetPayload['from']), to: getUTCTimestamp(widgetPayload['to']) };

const buildTrendsExportBody = (
    selectedWidgetClone: any,
    selectedPayload: any,
    widgetPayloadFn: any,
    payloadKey: string,
    selectedEntities?: any
): any => {
    const isLoadGroup = payloadKey !== 'trends';
    const widgetPayload = widgetPayloadFn(selectedWidgetClone, selectedEntities);
    return {
        exportRequests: isLoadGroup ? selectedPayload?.channelIds : widgetPayload['payload'],
        ...buildTrendsExportRange(isLoadGroup, selectedPayload, widgetPayload),
        types: ['DOWNLOAD'],
        chartType: CHART_TYPE[selectedWidgetClone?.type ?? 'line'],
        loadTrend: isLoadGroup,
    };
};

const buildDurationTrendsPayload = (
    widgetType: string,
    payloadKey: string,
    selectedWidgetClone: any,
    widgetPayloadFn: any,
    trendsPayload: any,
    selectedEntities?: any
): any => {
    const base =
        widgetType === 'load_group_trends'
            ? { payload: trendsPayload?.payload }
            : JSON.parse(JSON.stringify(widgetPayloadFn(selectedWidgetClone, selectedEntities)));
    base['from'] = trendsPayload?.activeDuration?.from;
    base['to'] = trendsPayload?.activeDuration?.to;
    base['loadTrend'] = payloadKey !== 'trends';
    return base;
};

const getWidgetFilterTimeRange = (
    widgetTypeId: string,
    timezone: any,
    calendarWeek: any,
    eventValue: any
): { from: any; to: any; prevFrom: any; prevTo: any } =>
    widgetTypeId === 'load_trends'
        ? timeStampHandler(timezone, calendarWeek, eventValue, null, null, 'load_trends')
        : timeStampHandler(timezone, calendarWeek, eventValue);

const resolveCustomEndpoint = (widgetTypeId: string, type: any): any =>
    customEndpointSelector(widgetTypeId, type)?.[0]?.['endpoint'];

const buildCsvDownloadPayload = (widgetTypeId: string, config: any, from: any, to: any, payloadFn: any): any => ({
    ...payloadFn[widgetTypeId]({ ...config }),
    startTime: from,
    endTime: to,
    timeFilter: config?.selectedTimePeriod?.toUpperCase(),
    unit: config?.unitType,
    download: true,
    email: [],
    fileType: 'CSV',
    sendEmail: false,
});

const buildActiveDashboardsParams = (groupId: any, currentOrg: any): any => {
    const orgId = currentOrg?.id;
    const restricted = groupId === orgId && currentOrg?.defaultGroupAccess === false;
    return {
        resourceType: 'GROUP',
        ...(restricted ? {} : { resourceTypeId: groupId ?? orgId }),
    };
};

const resolveWidgetLayout = (widgets: any, dashboards: any): any[] => {
    const nonPinnedWidgets = widgets?.filter((item: any) => !item?.config?.isPinned) ?? [];
    const baseLayout = dashboards?.data?.widgetLayout ?? [];
    const layoutById = new Map<string, any>(baseLayout.map((item: any) => [String(item?.i), item]));

    if (!nonPinnedWidgets.length) return [];

    return nonPinnedWidgets.map((item: any, index: number) => {
        const widgetId = item.id;
        const base = layoutById.get(String(widgetId)) ?? {};
        const { h = 6, minH = SECONDARY_MIN_GRID_ROWS, w = 49, x = 0, y = index * 7 } = base;

        return {
            ...base,
            i: widgetId,
            h,
            minH,
            minW: 14,
            moved: false,
            static: false,
            w,
            x,
            y,
        };
    });
};

const findWidgetById = (widgets: any, widgetId: any): any => widgets?.find((item: any) => item?.id === widgetId);

const getWidgetTypeId = (widget: any): any => widget?.widgetType?.id;

const cloneWidgetConfig = (widget: any): any => JSON.parse(JSON.stringify(widget?.config ?? {}));

const cloneWholeWidget = (widget: any): any => JSON.parse(JSON.stringify(widget ?? {}));

const filterPinnedWidgets = (widgets: any): any[] => widgets?.filter((item: any) => item?.config?.isPinned) ?? [];

const resolveLoadGroupStateCandidate = (message: any): any =>
    message?.groupState ?? message?.loadState ?? message?.state;

const parseLoadGroupStateFromMessage = (message: any): GroupState | null =>
    parseLoadGroupStateString(resolveLoadGroupStateCandidate(message)) ??
    parseBooleanLoadGroupState(message?.isEnabled) ??
    parseBooleanLoadGroupState(message?.isGroupEnabledOrDisabled);

const isDefaultDetailsCardType = (widgetTypeId: any): boolean =>
    widgetTypeId === 'details' || widgetTypeId === 'properties' || widgetTypeId === 'settings';

const hasWidgetViewAllRow = (config: any): boolean =>
    Boolean(config?.viewAllButton || config?.displayViewBar || config?.isViewAllDisplayed);

const getPrimarySecondaryCounts = (config: any): { primary: number; secondary: number } => ({
    primary: config?.primary?.length ?? 0,
    secondary: config?.secondary?.length ?? 0,
});

const isPrimaryWithSingleSecondary = (config: any): boolean => {
    const { primary, secondary } = getPrimarySecondaryCounts(config);
    return primary > 0 && secondary === 1;
};

const isMixedChannelConfig = (config: any): boolean => {
    const { primary, secondary } = getPrimarySecondaryCounts(config);
    return primary > 0 && secondary > 0;
};

const computeMinRowsForWidget = (widget: any): number => {
    const isDefaultCard = isDefaultDetailsCardType(getWidgetTypeId(widget));
    const hasViewAll = hasWidgetViewAllRow(widget?.config);
    if (!isDefaultCard || hasViewAll) return SECONDARY_MIN_GRID_ROWS;
    if (isPrimaryWithSingleSecondary(widget?.config)) return SECONDARY_MIN_GRID_ROWS;
    return 1;
};

const calculateAdjustedWidgetHeight = (
    height: number,
    minRows: number,
    isDefaultCard: boolean,
    hasViewAll: boolean,
    isMixed: boolean
): number => {
    if (minRows === 1) return Math.max(0, height - 18);
    if (isDefaultCard && !hasViewAll && isMixed) return Math.max(0, height - 16);
    if (isDefaultCard && !hasViewAll) return Math.max(0, height - 12);
    return height;
};

const computeNeededWidgetRows = (adjustedHeight: number, minRows: number): number =>
    Math.max(minRows, Math.ceil((adjustedHeight + SECONDARY_CARD_GAP_Y) / (GRID_ROWS + SECONDARY_CARD_GAP_Y)));

const matchesCommandChannel = (ch: any, commandBarData: any): boolean =>
    ch?.deviceId === commandBarData?.deviceId && ch?.channelId === commandBarData?.channelId;

const applyLoadingFlagToWidgetConfig = (loadingConfig: any, commandBarData: any): void => {
    if (loadingConfig?.secondary?.[0]?.channels) {
        loadingConfig.secondary.forEach((group: any) => {
            group?.channels?.forEach((ch: any) => {
                if (matchesCommandChannel(ch, commandBarData)) ch.isLoading = true;
            });
        });
        return;
    }
    if (Array.isArray(loadingConfig?.secondary)) {
        const idx = loadingConfig.secondary.findIndex((ch: any) => matchesCommandChannel(ch, commandBarData));
        if (idx !== -1) loadingConfig.secondary[idx].isLoading = true;
    }
};

const removeLoadingKeyFromClone = (widgetClone: any): void => {
    widgetClone?.secondary?.forEach((channel: any) => {
        channel?.channels?.forEach((item: any) => {
            if (item?.isLoading) delete item.isLoading;
        });
    });
};

const getProfileTimezoneInfo = (profileDetails: any): { timezone: any; calendarWeek: any } => ({
    timezone: profileDetails?.data?.timezone ?? systemTimezone,
    calendarWeek: profileDetails?.data?.calendarWeek,
});

const getTrendsApiEndpoint = (widgetType: string): string =>
    widgetType === 'trends' ? 'getDeviceTrends' : 'getLoadGroupTrends';

const computeTrendsDurationHours = (activeDuration: any): number =>
    (activeDuration?.to - activeDuration?.from) / 1000 / 60 / 60;

const mapGroupToSelectedNode = (item: any): any => ({
    id: item?.id,
    name: item?.name,
    deviceName: item?.deviceName,
    groupPath: item?.groupPath,
});

const buildMapApiFilters = (config: any, groupId: any): any => {
    const isMulti = config?.filters && config?.multiDevice;
    return isMulti ? config?.filters : { ...config?.filters, groups: [groupId] };
};

const buildMapApiPayload = (bounds: any, config: any, groupId: any): any => ({
    ...bounds,
    filters: { ...buildMapApiFilters(config, groupId) },
});

const getFirstDeviceListItem = (item: any): any => item?.deviceList?.[0];

const mapDeviceItemToLocation = (item: any): any => {
    const firstDevice = getFirstDeviceListItem(item);
    return {
        coordinates: item.coordinates,
        deviceCount: item.deviceCount,
        name: firstDevice?.name ?? '',
        id: firstDevice?.id,
        tileKey: item?.tileKey,
        deviceList: item?.deviceList,
        groupByStatus: item?.groupByStatus,
    };
};

const mergeMapSecondaryData = (existing: any[], newData: any[]): any[] => {
    const mergedMap = new Map(existing.map((item: any) => [item.tileKey, item]));
    newData.forEach((item: any) => mergedMap.set(item.tileKey, item));
    return Array.from(mergedMap.values());
};

const buildTrendPointFromMqttMessage = (message: any): any => ({
    timestamp: message?.timestamp,
    channelId: message?.key,
    deviceId: message?.deviceId,
    unit: message?.unit,
    value: +message?.value,
});

const isTrendsWidgetType = (widgetTypeId: any): boolean =>
    widgetTypeId === 'trends' || widgetTypeId === 'load_group_trends';

const FILTER_WIDGET_TYPES = new Set([
    'consumption_breakdown',
    'metrics',
    'load_trends',
    'weather',
    'aggregated_trends',
]);

const buildMapWidgetProps = (widget: any, onIdle: any): any => {
    if (getWidgetTypeId(widget) !== 'map') return {};
    return {
        countKey: 'deviceCount',
        widgetData: widget?.config,
        onIdle,
        widgetId: widget?.id,
        clustering: false,
    };
};

const buildScoreWidgetProps = (widget: any, scoreModalRef: any): any => {
    if (getWidgetTypeId(widget) !== 'score') return {};
    return {
        learnMoreCallback: (): void => {
            scoreModalRef?.current?.onClick();
        },
    };
};

type TrendsHandlers = {
    onTrendsPayload: (payload: any) => void;
    onDownloadTrends: (widgetId: any, channelIds?: any) => void;
    onLegendsPayload: (payload: any) => void;
};

const buildTrendsWidgetProps = (widget: any, handlers: TrendsHandlers): any => {
    if (!isTrendsWidgetType(getWidgetTypeId(widget))) return {};
    return {
        payloadChangeHandler: (widgetPayload: any): void =>
            handlers.onTrendsPayload({ ...widgetPayload, widgetId: widget?.id }),
        downloadHandler: (channelIds?: any): void => handlers.onDownloadTrends(widget?.id, channelIds),
        handlerLegendsPayload: (legends: any): void => handlers.onLegendsPayload({ ...legends, widgetId: widget?.id }),
    };
};

const buildCommandBarWidgetProps = (widget: any, onCommand: any): any => {
    if (getWidgetTypeId(widget) !== 'command_bar') return {};
    return {
        commandHandler: (data: any): void => onCommand(data, widget?.id),
    };
};

const buildLoadsWidgetProps = (widget: any, onLoads: any): any => {
    if (getWidgetTypeId(widget) !== 'loads') return {};
    return {
        loadsHandler: (data: any): void => onLoads(data, widget?.id),
    };
};

type LoadGroupsHandlers = {
    onLoadGroupToggle: (loadGroup: any, newState: GroupState, widgetId: string) => Promise<void> | void;
    loadGroupStates: Record<string, GroupState>;
    openLoadGroupsModal: (args: { widgetId: string; isMultiDevice: boolean; initialSelectedIds: string[] }) => void;
    isWidgetUpdating: boolean;
    onCreateLoadGroup: () => void;
};

const buildLoadGroupsWidgetProps = (widget: any, handlers: LoadGroupsHandlers): any => {
    if (getWidgetTypeId(widget) !== 'load_groups') return {};
    return {
        onLoadGroupToggle: (loadGroup: any, newState: GroupState, loadWidgetId: string): void =>
            handlers.onLoadGroupToggle(loadGroup, newState, loadWidgetId) as any,
        loadGroupStates: handlers.loadGroupStates,
        onOpenLoadGroupsModal: handlers.openLoadGroupsModal,
        isWidgetUpdating: handlers.isWidgetUpdating,
        onCreateLoadGroup: handlers.onCreateLoadGroup,
    };
};

const buildFilterWidgetProps = (widget: any, widgets: any, onFilter: any): any => {
    if (!FILTER_WIDGET_TYPES.has(getWidgetTypeId(widget))) return {};
    return {
        widgetFilter: (event: any, id: any): void => {
            onFilter(event, id, findWidgetById(widgets, id));
        },
    };
};

const buildAggregatedTrendsWidgetProps = (
    widget: any,
    widgets: any,
    handlers: { onUnitFilter: any; onDownloadCsv: any }
): any => {
    if (getWidgetTypeId(widget) !== 'aggregated_trends') return {};
    return {
        unitFilter: (event: any, id: any): void => {
            handlers.onUnitFilter(event, id, findWidgetById(widgets, id));
        },
        downloadCSVHandler: (id: any): void => {
            handlers.onDownloadCsv(findWidgetById(widgets, id));
        },
    };
};

const buildWeatherWidgetProps = (widget: any, widgets: any, onRefresh: any): any => {
    if (getWidgetTypeId(widget) !== 'weather') return {};
    return {
        fetchWeatherData: (id: any): void => {
            onRefresh(id, findWidgetById(widgets, id));
        },
    };
};

const buildDynamicWidgetDataBase = (widget: any): any => ({
    internalCdnJsLink: widget?.widgetType?.internalCdnJsLink,
});

const buildDynamicWidgetEmptyPayload = (): any => ({
    primary: [],
    secondary: [],
    secondaryChannelCount: 6,
});

const buildGridWidgetExtras = (widget: any): any => ({
    widgetTypeName: widget?.widgetType?.name,
    id: widget?.id,
});

const buildDynamicWidgetData = (widget: any, includeGridExtras: boolean): any => {
    const { config, name, widgetType, updatedAt } = widget ?? {};
    if (!config) return { ...buildDynamicWidgetDataBase(widget), ...buildDynamicWidgetEmptyPayload(), updatedAt };
    const extras = includeGridExtras ? buildGridWidgetExtras(widget) : {};
    return {
        ...buildDynamicWidgetDataBase(widget),
        ...config,
        widgetName: name,
        widgetTypeId: widgetType?.id,
        updatedAt,
        ...extras,
    };
};

const WidgetLoaderCard = ({
    widgetTypeId,
    renderLoader,
}: {
    widgetTypeId: any;
    renderLoader: (type: any) => JSX.Element;
}): JSX.Element => (
    <Card sx={{ height: '100%' }}>
        <CardContent className="padding-0 custom-card-height">{renderLoader(widgetTypeId)}</CardContent>
    </Card>
);

const NotConfiguredCard = ({
    widget,
    translate,
    descriptionKey,
}: {
    widget: any;
    translate: (key: string) => string;
    descriptionKey: string;
}): JSX.Element => (
    <Card className="margin-left-0" sx={{ height: '100%' }}>
        <CardHeader
            title={
                <Typography variant="subtitle2" color="primary" fontWeight={'600'} fontSize={'14px'} padding={'16px'}>
                    {widget?.name ?? '-'}
                </Typography>
            }
            className="border-bottom-1 padding-0"
        />
        <Divider />
        <CardContent>
            <CustomEmptyState
                icon={<ErrorOutline className="height-100 width-100" />}
                title={`${widget?.name} ${translate('COMMON:NOT_CONFIGURED')}`}
                className="padding-y-80"
                description={<CustomTransComponent translationKey={descriptionKey} />}
            />
        </CardContent>
    </Card>
);

type PinnedItemHandlers = TrendsHandlers & {
    onMapConfig: (args: any) => any;
    scoreModalRef: any;
    onCommand: (data: any, widgetId: string) => void;
};

const PinnedWidgetItem = ({
    widget,
    isLoaded,
    renderLoader,
    handlers,
}: {
    widget: any;
    isLoaded: boolean;
    renderLoader: (type: any) => JSX.Element;
    handlers: PinnedItemHandlers;
}): JSX.Element => (
    <>
        {!isLoaded && <WidgetLoaderCard widgetTypeId={getWidgetTypeId(widget)} renderLoader={renderLoader} />}
        {isLoaded && (
            <DynamicWidget
                mode="pinned-view"
                widgetTypeId={getWidgetTypeId(widget)}
                {...buildMapWidgetProps(widget, handlers.onMapConfig)}
                {...buildScoreWidgetProps(widget, handlers.scoreModalRef)}
                {...buildTrendsWidgetProps(widget, handlers)}
                {...buildCommandBarWidgetProps(widget, handlers.onCommand)}
                widgetData={buildDynamicWidgetData(widget, false)}
            />
        )}
    </>
);

type GridItemHandlers = PinnedItemHandlers &
    LoadGroupsHandlers & {
        trendsRef: any;
        onHeightChange: (widgetId: string, height: number) => void;
        onLoads: (data: any, widgetId: string) => void;
        widgets: any;
        onWidgetFilter: (event: any, id: any, widget: any) => void;
        onUnitFilter: (event: any, id: any, widget: any) => void;
        onDownloadCsv: (widget: any) => void;
        onRefreshWeather: (id: any, widget: any) => void;
    };

const GridWidgetContent = ({
    widget,
    isLoaded,
    isApplicable,
    isConfigured,
    renderLoader,
    handlers,
    translate,
}: {
    widget: any;
    isLoaded: boolean;
    isApplicable: boolean;
    isConfigured: boolean;
    renderLoader: (type: any) => JSX.Element;
    handlers: GridItemHandlers;
    translate: (key: string) => string;
}): JSX.Element => {
    if (!isLoaded) {
        return <WidgetLoaderCard widgetTypeId={getWidgetTypeId(widget)} renderLoader={renderLoader} />;
    }
    if (!isApplicable) {
        return (
            <NotConfiguredCard
                widget={widget}
                translate={translate}
                descriptionKey={'VIRTUAL_LOAD:WIDGET_TYPE_NOT_SUPPORTED'}
            />
        );
    }
    if (!isConfigured) {
        return (
            <NotConfiguredCard
                widget={widget}
                translate={translate}
                descriptionKey={'VIRTUAL_LOAD:THIS_WIDGET_TYPE_NOT_CONFIGURED'}
            />
        );
    }
    return (
        <DynamicWidget
            mode="view"
            widgetRef={handlers.trendsRef}
            dropType="Group"
            widgetTypeId={getWidgetTypeId(widget)}
            onHeightChange={(h: number): void => handlers.onHeightChange(widget?.id, h)}
            {...buildMapWidgetProps(widget, handlers.onMapConfig)}
            {...buildScoreWidgetProps(widget, handlers.scoreModalRef)}
            {...buildTrendsWidgetProps(widget, handlers)}
            {...buildCommandBarWidgetProps(widget, handlers.onCommand)}
            {...buildLoadsWidgetProps(widget, handlers.onLoads)}
            {...buildLoadGroupsWidgetProps(widget, handlers)}
            {...buildFilterWidgetProps(widget, handlers.widgets, handlers.onWidgetFilter)}
            {...buildAggregatedTrendsWidgetProps(widget, handlers.widgets, handlers)}
            {...buildWeatherWidgetProps(widget, handlers.widgets, handlers.onRefreshWeather)}
            widgetData={buildDynamicWidgetData(widget, true)}
        />
    );
};

type EmptyStateHandlers = {
    theme: any;
    groupId: any;
    currentOrgId: any;
    modalRef: any;
    canCreateSystemDashboard: boolean;
    canCreateUserDashboard: boolean;
};

const NoDashboardsEmptyState = ({ handlers }: { handlers: EmptyStateHandlers }): JSX.Element => (
    <Stack alignItems={'center'} justifyContent={'center'} sx={{ height: 'calc(100vh - 200px)' }}>
        <CustomEmptyState
            icon={<Widgets fontSize="inherit" />}
            title={
                (<CustomTransComponent size={{ width: '380px' }} translationKey={'WIDGETS:NO_WIDGETS_FOUND'} />) as any
            }
            description={(<CustomTransComponent translationKey={'WIDGETS:NO_WIDGET_DESC'} />) as any}
            actions={
                (handlers.canCreateSystemDashboard || handlers.canCreateUserDashboard) && (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DesignServices />}
                        data-testid="group-summary-create-dashboard-btn"
                        sx={{
                            border: `1px solid ${handlers.theme?.palette?.primary?.main}`,
                            '&:hover': {
                                backgroundColor: handlers.theme?.palette?.primary?.[50],
                            },
                        }}
                        onClick={(): void =>
                            handlers.modalRef?.current?.handleModalAction?.(true, {
                                assignType: 'GROUP',
                                assignTypeIds: [handlers.groupId ?? handlers.currentOrgId ?? ''],
                            })
                        }
                    >
                        <CustomTransComponent
                            size={{ width: '140px' }}
                            translationKey={'DASHBOARDS:CREATE_DASHBOARD'}
                        />
                    </Button>
                )
            }
        />
    </Stack>
);

const NoWidgetsEmptyState = (): JSX.Element => (
    <Stack alignItems={'center'} justifyContent={'center'} sx={{ height: 'calc(100vh - 200px)' }}>
        <CustomEmptyState
            icon={<Widgets fontSize="inherit" />}
            title={
                (<CustomTransComponent size={{ width: '380px' }} translationKey={'WIDGETS:NO_WIDGETS_FOUND'} />) as any
            }
            description={(<CustomTransComponent translationKey={'WIDGETS:NO_WIDGET_DESC'} />) as any}
        />
    </Stack>
);

const shouldShowNoDashboardsState = (
    dashboards: any,
    isSuccess: boolean,
    isError: boolean,
    isLoading: boolean,
    isFetching: boolean,
    configLoading: boolean
): boolean => !dashboards?.data && (isSuccess || isError) && !isLoading && !isFetching && !configLoading;

const shouldShowNoWidgetsState = (
    dashboards: any,
    widgets: any,
    isLoading: boolean,
    isFetching: boolean,
    configLoading: boolean
): boolean => Boolean(dashboards?.data && !widgets?.length && !isLoading && !isFetching && !configLoading);

const extractDashboardsData = (dashboards: any): any => dashboards?.data;

const shouldSkipActiveDashboardsQuery = (currentOrg: any): boolean => !currentOrg?.id;

const getCurrentOrgId = (currentOrg: any): any => currentOrg?.id;

const shouldShowAnyLoader = (isLoading: boolean, isFetching: boolean, configLoading: boolean): boolean =>
    Boolean(isLoading || isFetching || configLoading);

const resolveGroupIdOrOrgId = (groupId: any, currentOrg: any): any => groupId ?? currentOrg?.id;

const getLoadGroupsModalMaxCount = (widgets: any, widgetId: string): any =>
    findWidgetById(widgets, widgetId)?.config?.maxCount;

export const GroupSummary = (): JSX.Element => {
    const theme: any = useTheme();
    const { t } = useTranslation();
    const { groupId } = useParams();
    const dispatch = useAppDispatch();
    const modalRef = React.useRef<any>(null);
    const trendsRef = React.useRef<any>(null);
    const currentOrg = useTypedSelector(selectedOrg);
    const widgets = useTypedSelector(widgetList);
    const loadingState = useTypedSelector((rootState) => rootState.commonDashboard.loaders);
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canCreateSystemDashboard = hasPermission('dashboard-management_create-system-dashboards');
    const canCreateUserDashboard = hasPermission('dashboard-management_create-user-dashboards');
    const scoreModalRef = React.useRef<any>(null);

    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);

    const topics = useTypedSelector(mqttTopics);
    const { data: profileDetails } = useGetUserProfileQuery({}) as ApiEndpointType<ApiResponseType<UserProfile>>;
    const [updateLoadGroups] = useUpdateLoadGroupsMutation();
    const [updateWidget, { isLoading: isWidgetUpdateLoading }] = useUpdateWidgetMutation();
    const [loadGroupsModalState, setLoadGroupsModalState] = React.useState<{
        isVisible: boolean;
        widgetId: string;
        isMultiDevice: boolean;
        initialSelectedIds: string[];
    }>({
        isVisible: false,
        widgetId: '',
        isMultiDevice: false,
        initialSelectedIds: [],
    });
    const [isCreateLoadGroupModalOpen, setIsCreateLoadGroupModalOpen] = React.useState(false);
    const [loadGroupStates, setLoadGroupStates] = React.useState<Record<string, GroupState>>({});
    const [measuredHeights, setMeasuredHeights] = React.useState<Record<string, number>>({});
    const loadGroupFallbackTimeoutRefs = React.useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
    const lastLoadGroupMqttAtRef = React.useRef<Record<string, number>>({});

    const getMinRowsForWidget = React.useCallback(
        (widgetId: string): number => computeMinRowsForWidget(findWidgetById(widgets, widgetId)),
        [widgets]
    );

    const handleWidgetHeightChange = React.useCallback(
        (widgetId: string, height: number): void => {
            // react-grid-layout: h rows => h * rowHeight + (h - 1) * marginY
            const minRows = getMinRowsForWidget(widgetId);
            const widget = findWidgetById(widgets, widgetId);
            const isDefaultCard = isDefaultDetailsCardType(getWidgetTypeId(widget));
            const hasViewAll = hasWidgetViewAllRow(widget?.config);
            const isMixed = isMixedChannelConfig(widget?.config);
            const adjustedHeight = calculateAdjustedWidgetHeight(height, minRows, isDefaultCard, hasViewAll, isMixed);
            const neededH = computeNeededWidgetRows(adjustedHeight, minRows);
            setMeasuredHeights((prev) => (prev[widgetId] === neededH ? prev : { ...prev, [widgetId]: neededH }));
        },
        [getMinRowsForWidget, widgets]
    );

    const markLoadGroupPending = React.useCallback((loadGroupId: number | string): void => {
        setLoadGroupStates((prev) => ({ ...prev, [String(loadGroupId)]: 'PENDING' }));
    }, []);

    const getLoadGroupStateFromMqttMessage = React.useCallback(parseLoadGroupStateFromMessage, []);

    const {
        data: dashboards,
        isLoading,
        isFetching,
        isSuccess,
        isError,
    }: any = useGetActiveDashboardsQuery(
        {
            params: buildActiveDashboardsParams(groupId, currentOrg),
        },
        {
            skip: shouldSkipActiveDashboardsQuery(currentOrg),
            refetchOnMountOrArgChange: true,
        }
    );

    const applyWidgetFilterSuccess = (
        response: any,
        widgetToUpdate: any,
        id: any,
        widgetTypeId: string,
        eventValue: any
    ): void => {
        dispatch(
            setWidgetList({
                data: {
                    ...widgetToUpdate,
                    config: {
                        ...widgetToUpdate.config,
                        secondary: response?.data?.data,
                        ...(isLoadTrendsTimeFilterWidget(widgetTypeId) && {
                            selectedTimePeriod: eventValue,
                        }),
                    },
                },
                widgetId: id,
            })
        );
    };

    const dispatchWidgetFilterRequest = (
        endpoint: string,
        payloadData: any,
        widgetToUpdate: any,
        id: any,
        widgetTypeId: string,
        eventValue: any
    ): void => {
        dispatch((api as any)?.endpoints?.[endpoint]?.initiate(payloadData, { forceRefetch: true }))
            .then((response: any) => {
                if (response?.isError) {
                    handleError(widgetToUpdate, id);
                } else {
                    applyWidgetFilterSuccess(response, widgetToUpdate, id, widgetTypeId, eventValue);
                }
            })
            .catch((error: any) => {
                console.error('error: ', error);
                handleError(widgetToUpdate, id);
            });
    };

    const handleWidgetFilter = (event: any, id: any, widgetToUpdate: any): void => {
        const widgetTypeId = getWidgetTypeId(widgetToUpdate);
        const eventValue = event?.target?.value;
        const { timezone, calendarWeek } = getProfileTimezoneInfo(profileDetails);
        const { from, to, prevFrom, prevTo } = getWidgetFilterTimeRange(
            widgetTypeId,
            timezone,
            calendarWeek,
            eventValue
        );
        const { config } = widgetToUpdate;
        const data = payload[widgetTypeId]({ ...config });
        const normalizedTimeFilter = eventValue?.toUpperCase();
        const payloadData = buildTimeRangePayload(
            widgetTypeId,
            data,
            config,
            { from, to, prevFrom, prevTo },
            normalizedTimeFilter
        );
        const endpoint: any = resolveCustomEndpoint(widgetTypeId, config?.type);
        if (!endpoint) {
            console.error('Invalid endpoint');
            return;
        }
        dispatchWidgetFilterRequest(endpoint, payloadData, widgetToUpdate, id, widgetTypeId, eventValue);
    };

    const handleError = (widgetToUpdate: any, id: any): void => {
        dispatch(
            setWidgetList({
                data: {
                    ...widgetToUpdate,
                    config: {
                        ...widgetToUpdate.config,
                        secondary: {},
                    },
                },
                widgetId: id,
            })
        );
    };

    const buildGSUnitTypePayloadData = (widgetToUpdate: any, config: any, eventValue: any): any => {
        const widgetTypeId = getWidgetTypeId(widgetToUpdate);
        const { timezone, calendarWeek } = getProfileTimezoneInfo(profileDetails);
        const { from, to } = timeStampHandler(timezone, calendarWeek, config?.selectedTimePeriod);
        return {
            ...payload[widgetTypeId]({ ...config }),
            startTime: from,
            endTime: to,
            timeFilter: config?.selectedTimePeriod?.toUpperCase(),
            unit: eventValue,
        };
    };

    const handleGSUnitTypeResponse = (response: any, widgetToUpdate: any, id: any, eventValue: any): void => {
        const secondary = response?.isError ? {} : response?.data?.data;
        const extraConfig = response?.isError ? {} : { unitType: eventValue };
        dispatch(
            setWidgetList({
                data: { ...widgetToUpdate, config: { ...widgetToUpdate.config, secondary, ...extraConfig } },
                widgetId: id,
            })
        );
    };

    const dispatchUnitTypeFilterRequest = (
        endpoint: any,
        payloadData: any,
        widgetToUpdate: any,
        id: any,
        eventValue: any
    ): void => {
        dispatch((api as any)?.endpoints?.[endpoint]?.initiate(payloadData))
            .then((response: any) => handleGSUnitTypeResponse(response, widgetToUpdate, id, eventValue))
            .catch((error: any) => {
                console.error('error: ', error);
            });
    };

    const handleUnitTypeFilter = (event: any, id: any, widgetToUpdate: any): void => {
        const { config } = widgetToUpdate;
        const eventValue = event?.target?.value;
        const payloadData = buildGSUnitTypePayloadData(widgetToUpdate, config, eventValue);
        const widgetTypeId = getWidgetTypeId(widgetToUpdate);
        const endpoint = customEndpointSelector(widgetTypeId, config?.type)?.[0]?.['endpoint'];
        dispatchUnitTypeFilterRequest(endpoint, payloadData, widgetToUpdate, id, eventValue);
    };

    const handleGSCsvDownloadSuccess = (response: any): void => {
        const fetchUrl: any = response?.data?.data?.filter((dataUrl: any) => dataUrl?.reportType === 'Download');
        downloadFile(fetchUrl[0]?.url, 'Aggregated_Trends.csv');
        dispatch(setMessageContent({ isOpen: true, message: fetchUrl[0]?.message, duration: 3000 }));
    };

    const dispatchCsvDownloadRequest = (endpoint: string, payloadData: any): void => {
        dispatch((api as any)?.endpoints?.[endpoint]?.initiate(payloadData))
            .then((response: any) => handleGSCsvDownloadSuccess(response))
            .catch((error: any) => {
                console.error('error: ', error);
            });
    };

    const handleDownloadCSV = (widgetToUpdate: any): void => {
        const { config } = widgetToUpdate;
        const widgetTypeId = getWidgetTypeId(widgetToUpdate);
        const { timezone, calendarWeek } = getProfileTimezoneInfo(profileDetails);
        const { from, to } = timeStampHandler(timezone, calendarWeek, config?.selectedTimePeriod);
        const endpoint = customEndpointSelector(widgetTypeId, config?.downloadType)?.[0]?.['endpoint'];
        if (!endpoint) return;
        const payloadData = buildCsvDownloadPayload(widgetTypeId, config, from, to, payload);
        dispatchCsvDownloadRequest(endpoint, payloadData);
    };

    const handleRefreshWeatherData = (id: any, widgetToUpdate: any): void => {
        const { config } = widgetToUpdate;
        const widgetTypeId = widgetToUpdate?.widgetType?.id;
        const endpoint = customEndpointSelector(widgetTypeId, config?.type)?.[0]?.['endpoint'];
        dispatch(
            (api as any)?.endpoints?.[endpoint]?.initiate(
                { ...payload[widgetTypeId]({ ...config }) },
                { forceRefetch: true }
            )
        )
            .then((response: any) => {
                const secondary = response?.isError ? {} : response?.data?.data;
                dispatch(
                    setWidgetList({
                        data: { ...widgetToUpdate, config: { ...widgetToUpdate.config, secondary } },
                        widgetId: id,
                    })
                );
            })
            .catch((error: any) => {
                console.error('error: ', error);
            });
    };

    const resolvedGroupId = resolveGroupIdOrOrgId(groupId, currentOrg);
    const { isLoading: configLoading } = useCommonWidgetConfiguration(extractDashboardsData(dashboards), {
        groupId: resolvedGroupId,
    });
    const { payload, response: widgetResponse } = useGetWidgetDetails({
        selectorType: 'commonDashboard',
        dataGetter: {
            groupId: resolvedGroupId,
        },
    });

    const { handleDeviceCommands, handleDeviceLoads } = useDashboardCommonHandlers();

    const dispatchGetLoadGroupsList = (loadGroupsPayload: any): Promise<any> =>
        dispatch((api as any)?.endpoints?.['getLoadGroupsList']?.initiate(loadGroupsPayload, { forceRefetch: true }));

    const applyGetLoadGroupsListResponse = (response: any, widgetId: string): void => {
        if (response?.data) widgetResponse['load_groups']?.([response], widgetId);
    };

    const refreshLoadGroupsWidget = React.useCallback(
        async (widgetId: string): Promise<void> => {
            const selectedWidget = findWidgetById(widgets, widgetId);
            const loadGroupsPayload = selectedWidget?.config
                ? payload?.['load_groups']?.(selectedWidget.config)
                : undefined;
            if (!loadGroupsPayload) return;
            const response = await dispatchGetLoadGroupsList(loadGroupsPayload);
            applyGetLoadGroupsListResponse(response, widgetId);
        },
        [dispatch, payload, widgetResponse, widgets]
    );

    const clearLoadGroupPendingState = (loadGroupIdStr: string): void => {
        setLoadGroupStates((prev) => {
            if (prev[loadGroupIdStr] !== 'PENDING') return prev;
            const next = { ...prev };
            delete next[loadGroupIdStr];
            return next;
        });
    };

    const scheduleLoadGroupFallback = (loadGroup: any, widgetId: string, toggleStartedAt: number): void => {
        const loadGroupIdStr = String(loadGroup.id);
        const timeoutKey = `${widgetId}:${loadGroupIdStr}`;
        const existingTimeout = loadGroupFallbackTimeoutRefs.current[timeoutKey];
        if (existingTimeout) clearTimeout(existingTimeout);
        loadGroupFallbackTimeoutRefs.current[timeoutKey] = setTimeout(() => {
            const lastMqttAt = lastLoadGroupMqttAtRef.current[loadGroupIdStr] ?? 0;
            if (lastMqttAt >= toggleStartedAt) return;
            void refreshLoadGroupsWidget(widgetId).finally(() => clearLoadGroupPendingState(loadGroupIdStr));
        }, 60_000);
    };

    const dispatchLoadGroupToggleToast = (loadGroup: any, isEnabled: boolean): void => {
        dispatch(
            setMessageContent({
                isOpen: true,
                message: `${loadGroup?.name} ${isEnabled ? 'turn on' : 'turn off'} initiated`,
                duration: 3000,
            })
        );
    };

    const handleLoadGroupToggle = async (loadGroup: any, newState: GroupState, widgetId?: string): Promise<void> => {
        if (!loadGroup?.id) return;
        const isEnabled = newState === 'ENABLED';
        const { error }: any = await updateLoadGroups({
            loadGroupId: loadGroup.id,
            body: { performGroupSwitchAction: true, isGroupEnabledOrDisabled: isEnabled },
        });
        if (error) throw error;
        dispatchLoadGroupToggleToast(loadGroup, isEnabled);
        markLoadGroupPending(loadGroup.id);
        if (!widgetId) return;
        scheduleLoadGroupFallback(loadGroup, widgetId, Date.now());
    };

    const refreshLoadGroupsWithConfig = async (config: any, widgetId: string): Promise<void> => {
        const loadGroupsPayload = payload?.['load_groups']?.(config);
        if (!loadGroupsPayload) return;
        const response = await dispatchGetLoadGroupsList(loadGroupsPayload);
        applyGetLoadGroupsListResponse(response, widgetId);
    };

    const dispatchLoadGroupsAddedToast = (count: number): void => {
        dispatch(
            setMessageContent({
                isOpen: true,
                message: `${count} ${t('WIDGETS:LOADS_GROUPS.LOAD_GROUPS_HAVE_BEEN_ADDED_SUCCESSFULLY')}`,
                duration: 3000,
            })
        );
    };

    const handleLoadGroupsSelect = async (selectedGroups: any[], widgetId: string): Promise<void> => {
        const selectedWidget = findWidgetById(widgets, widgetId);
        if (!selectedWidget) return;
        const configClone = cloneWidgetConfig(selectedWidget);
        configClone.selectedNodes = selectedGroups.map(mapGroupToSelectedNode);
        delete configClone.secondary;
        const { error }: any = await updateWidget({
            widgetId,
            body: {
                config: configClone,
                sourceId: selectedWidget?.sourceId ?? null,
                sourceName: selectedWidget?.sourceName ?? null,
            },
        });
        if (error) throw error;
        await refreshLoadGroupsWithConfig(configClone, widgetId);
        dispatchLoadGroupsAddedToast(selectedGroups?.length);
    };

    const openLoadGroupsModal = (args: {
        widgetId: string;
        isMultiDevice: boolean;
        initialSelectedIds: string[];
    }): void => {
        setLoadGroupsModalState({
            isVisible: true,
            widgetId: args.widgetId,
            isMultiDevice: args.isMultiDevice,
            initialSelectedIds: args.initialSelectedIds,
        });
    };

    const closeLoadGroupsModal = (): void => {
        setLoadGroupsModalState((prev) => ({ ...prev, isVisible: false }));
    };

    // Always keep a fresh reference to widgets for use in callbacks
    const widgetsRef = React.useRef(widgets);
    React.useEffect(() => {
        widgetsRef.current = widgets;
    }, [widgets]);

    // Track pending command poll timeouts so they can be cancelled on unmount
    // or before scheduling a new poll for the same widget.
    const commandPollTimeoutRefs = React.useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
    const lastCommandMqttAtByWidgetRef = React.useRef<Record<string, number>>({});
    const lastAvailableCommandsFetchAtByWidgetRef = React.useRef<Record<string, number>>({});

    // After sendDeviceCommand HTTP settles, keep the spinner alive while we poll for
    // the updated available controls, then update button names once the response arrives.
    // This is the HTTP fallback when MQTT is not available in the browser.

    const dispatchExecutedToast = (commandBarData: any): void => {
        dispatch(
            setMessageContent({
                isOpen: true,
                message: `${commandBarData?.displayName?.toLowerCase()} - executed`,
            })
        );
    };

    const pollAvailableCommandsAfterAction = (widgetId: string, commandStartedAt: number): void => {
        const lastMqttAt = lastCommandMqttAtByWidgetRef.current[widgetId] ?? 0;
        if (lastMqttAt >= commandStartedAt) return;

        const freshConfig = widgetsRef.current?.find((w: any) => w?.id === widgetId)?.config;
        if (!freshConfig) return;

        dispatch(
            (api as any)?.endpoints?.['getAvailableCommands']?.initiate(
                payload['command_bar-getAvailableCommands'](
                    freshConfig,
                    widgetsRef.current?.find((w: any) => w?.id === widgetId)?.selectedEntities
                ),
                { forceRefetch: true }
            )
        ).then((response: any) => {
            if (!response?.error) {
                widgetResponse['command_bar']([response], widgetId);
            } else {
                const fallback = JSON.parse(
                    JSON.stringify(widgetsRef.current?.find((w: any) => w?.id === widgetId)?.config ?? {})
                );
                dispatch(configureSummaryValues({ widgetId, config: fallback }));
            }
        });
    };

    const handleCommandBarAction = React.useCallback(
        (commandBarData: any, widgetId: string): void => {
            void handleDeviceCommands(commandBarData, widgetId).then((result) => {
                if (!result?.isSuccess) return;

                if (!result?.hasMessage) {
                    dispatchExecutedToast(commandBarData);
                }

                const loadingConfig = cloneWidgetConfig(findWidgetById(widgetsRef.current, widgetId));
                applyLoadingFlagToWidgetConfig(loadingConfig, commandBarData);
                dispatch(configureSummaryValues({ widgetId, config: loadingConfig }));

                const existingTimeout = commandPollTimeoutRefs.current[widgetId];
                if (existingTimeout) {
                    clearTimeout(existingTimeout);
                }

                const commandStartedAt = Date.now();
                const fallbackDelayMs = mqttConnectionStatus ? 30000 : 10000;
                commandPollTimeoutRefs.current[widgetId] = setTimeout(() => {
                    pollAvailableCommandsAfterAction(widgetId, commandStartedAt);
                }, fallbackDelayMs);
            });
        },
        [handleDeviceCommands, dispatch, payload, widgetResponse, mqttConnectionStatus]
    );

    React.useEffect(() => {
        dispatch(resetDashboard());
    }, [groupId]);

    React.useEffect(() => {
        dispatch(resetLocalDashboard());
        dispatch(resetTrendsLegendData());
    }, []);

    React.useEffect(() => () => dispatch(resetDashboard()), []);

    React.useEffect(
        () => () => {
            Object.values(commandPollTimeoutRefs?.current ?? {}).forEach((timeoutId) => {
                if (timeoutId) clearTimeout(timeoutId);
            });
            Object.values(loadGroupFallbackTimeoutRefs?.current ?? {}).forEach((timeoutId) => {
                if (timeoutId) clearTimeout(timeoutId);
            });
        },
        []
    );

    React.useEffect(() => {
        if (dashboards) {
            dispatch(setDashboardId(dashboards?.data?.id));
        }
    }, [dashboards]);

    // Ref to track last map config call and prevent duplicates
    const lastMapConfigArgsRef = React.useRef<string | null>(null);

    const applyMapDataToClone = (selectedWidgetClone: any, newData: any[], appendData: boolean): void => {
        if (appendData) {
            const existingData = selectedWidgetClone?.config?.secondary ?? [];
            selectedWidgetClone['config']['secondary'] = mergeMapSecondaryData(existingData, newData);
        } else {
            selectedWidgetClone['config']['secondary'] = newData;
        }
    };

    const handleMapConfig = (args: any): any => {
        const selectedWidgetClone = cloneWholeWidget(findWidgetById(widgets, args.widgetId));
        const apiPayload = buildMapApiPayload(args?.bounds, selectedWidgetClone?.config, groupId);
        const apiPayloadKey = JSON.stringify(apiPayload);
        if (lastMapConfigArgsRef.current === apiPayloadKey) return;
        lastMapConfigArgsRef.current = apiPayloadKey;

        dispatch((api as any)?.endpoints?.['deviceMap']?.initiate(apiPayload))?.then(({ data }: any) => {
            if (!data) return;
            const newData = data?.data?.map(mapDeviceItemToLocation);
            applyMapDataToClone(selectedWidgetClone, newData, Boolean(args?.appendData));
            dispatch(configureSummaryValues({ widgetId: args?.widgetId, config: selectedWidgetClone?.config }));
        });
    };

    const dispatchExportTrends = (body: any, widgetType: any): void => {
        dispatch((api as any)?.endpoints?.['exportTrends']?.initiate({ body, params: { type: 'CSV' } })).then(
            (response: any) => {
                if (response?.data?.data) {
                    downloadFileFromLink(response?.data?.data, widgetType);
                    dispatch(setMessageContent({ isOpen: true, message: t('COMMON:FILE_DOWNLOAD_SUCCESS') }));
                }
            }
        );
    };

    const downloadTrends = (widgetId: any, selectedPayload: any): void => {
        const target = findWidgetById(widgets, widgetId);
        const selectedWidgetClone = cloneWidgetConfig(target);
        const widgetType = getWidgetTypeId(target);
        const payloadKey = getTrendsPayloadKey(widgetType);
        if (!selectedWidgetClone) return;
        const body = buildTrendsExportBody(
            selectedWidgetClone,
            selectedPayload,
            payload[payloadKey],
            payloadKey,
            target?.selectedEntities
        );
        dispatchExportTrends(body, widgetType);
    };

    const dispatchTrendsDurationRequest = (widgetType: string, newPayload: any, trendsPayload: any): void => {
        dispatch(
            (api as any)?.endpoints?.[getTrendsApiEndpoint(widgetType)]?.initiate(newPayload, { forceRefetch: true })
        ).then((response: any) => {
            widgetResponse[widgetType]([response], trendsPayload?.widgetId, {
                duration: computeTrendsDurationHours(trendsPayload?.activeDuration),
            });
        });
    };

    const handleTrendsDurationPayload = (trendsPayload: any): void => {
        const target = findWidgetById(widgets, trendsPayload?.widgetId);
        const selectedWidgetClone = cloneWidgetConfig(target);
        const widgetType = getWidgetTypeId(target);
        const payloadKey = getTrendsPayloadKey(widgetType);
        const newPayload = buildDurationTrendsPayload(
            widgetType,
            payloadKey,
            selectedWidgetClone,
            payload[payloadKey],
            trendsPayload,
            target?.selectedEntities
        );
        if (!selectedWidgetClone) return;
        dispatch(removeSummaryWidgetLoader(trendsPayload?.widgetId));
        dispatchTrendsDurationRequest(widgetType, newPayload, trendsPayload);
    };

    const getLegendsPayloadKey = (widgetType: string): string =>
        widgetType === 'load_group_trends' ? 'load_group_trends-getLoadGroupTrends' : 'trends';

    const dispatchLegendsTrendsRequest = (
        widgetType: string,
        newPayload: any,
        selectionPayload: any,
        selectedWidgetClone: any
    ): void => {
        dispatch(
            (api as any)?.endpoints?.[getTrendsApiEndpoint(widgetType)]?.initiate(newPayload, {
                forceRefetch: true,
            })
        ).then((response: any) => {
            widgetResponse[selectedWidgetClone?.widgetType?.id]([response], selectionPayload?.widgetId, {
                selectedWidget: selectedWidgetClone,
            });
        });
    };

    const applyLegendsSecondary = (selectedWidgetClone: any, selectionPayload: any, payloadKey: string): void => {
        if (!selectedWidgetClone?.config || payloadKey !== 'trends') return;
        selectedWidgetClone['config']['secondary'] = selectionPayload.payload;
        dispatch(configureSummaryValues({ widgetId: selectionPayload?.widgetId, config: selectedWidgetClone }));
    };

    const handleLegendsPayload = (selectionPayload: any): void => {
        const target = findWidgetById(widgets, selectionPayload?.widgetId);
        const selectedWidgetClone = cloneWholeWidget(target);
        const widgetType = getWidgetTypeId(target);
        const payloadKey = getLegendsPayloadKey(widgetType);
        applyLegendsSecondary(selectedWidgetClone, selectionPayload, payloadKey);
        const newPayload = buildDurationTrendsPayload(
            widgetType,
            payloadKey,
            selectedWidgetClone,
            payload[payloadKey],
            { payload: selectionPayload?.payload, activeDuration: selectionPayload?.activeDuration },
            target?.selectedEntities
        );
        if (!selectedWidgetClone?.config) return;
        dispatchLegendsTrendsRequest(widgetType, newPayload, selectionPayload, selectedWidgetClone);
    };

    const handleGSLoadGroupStateChange = (message: any): void => {
        if (message?.loadGroupId) {
            lastLoadGroupMqttAtRef.current[String(message.loadGroupId)] = Date.now();
        }
        const mqttState = getLoadGroupStateFromMqttMessage(message);
        setLoadGroupStates((prev) => {
            const next = { ...prev };
            if (next[message.loadGroupId] === 'PENDING') {
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: `${message?.loadGroupName} ${
                            mqttState === 'ENABLED' ? 'turn on' : 'turn off'
                        } completed`,
                    })
                );
            }
            if (message?.loadGroupId && mqttState) {
                next[message.loadGroupId] = mqttState;
            }
            return next;
        });
    };

    const isGSCommandBarWidgetForDevice = (item: any, selectedDeviceId: string): boolean =>
        item?.widgetType?.id === 'command_bar' &&
        item?.config?.secondary?.some((channelData: any) => channelData?.deviceId === selectedDeviceId);

    const dispatchAvailableCommandsRequest = (widgetClone: any, widgetId: any): void => {
        dispatch(
            (api as any)?.endpoints?.['getAvailableCommands']?.initiate(
                payload['command_bar-getAvailableCommands'](
                    widgetClone,
                    findWidgetById(widgets, widgetId)?.selectedEntities
                ),
                { forceRefetch: true }
            )
        ).then((response: any) => {
            widgetResponse['command_bar']([response], widgetId);
        });
    };

    const processGSCommandWidget = (widget: any): void => {
        const widgetId = widget?.id;
        const now = Date.now();
        lastCommandMqttAtByWidgetRef.current[widgetId] = now;
        const lastFetchAt = lastAvailableCommandsFetchAtByWidgetRef.current[widgetId] ?? 0;
        if (now - lastFetchAt < 500) return;
        lastAvailableCommandsFetchAtByWidgetRef.current[widgetId] = now;
        const widgetClone = JSON.parse(JSON.stringify(widget?.config));
        removeLoadingKeyFromClone(widgetClone);
        // Immediately clear any stuck isLoading from secondary before API response
        dispatch(configureSummaryValues({ widgetId, config: widgetClone }));
        dispatchAvailableCommandsRequest(widgetClone, widgetId);
    };

    const handleGSCommandActionMessage = (message: any, splitedArr: any): void => {
        const selectedDeviceId = splitedArr[splitedArr?.length - 1];
        const widgetsClone = JSON.parse(JSON.stringify(widgets));
        const filteredWidgets = widgetsClone?.filter((item: any) =>
            isGSCommandBarWidgetForDevice(item, selectedDeviceId)
        );
        if (message?.message) {
            dispatch(setMessageContent({ isOpen: true, message: message?.message }));
        }
        filteredWidgets?.forEach((widget: any) => processGSCommandWidget(widget));
    };

    const handleGSCommandTopicMessage = (message: any, messageTopic: string, splitedArr: any): void => {
        const type = message?.type;
        if (type === 'FAILED_LOAD_TRIGGER_FOR_LOAD_GROUP' && topics?.includes(messageTopic)) {
            dispatch(setFailedLoadTrigger(message));
        }
        if (type === 'LOAD_GROUP_STATE_CHANGE') {
            handleGSLoadGroupStateChange(message);
        }
        if (type === 'command' || type === 'runTest') {
            handleGSCommandActionMessage(message, splitedArr);
        }
    };

    const mqttResponseHandler = (message: any, messageTopic: string): void => {
        const splitedArr: any = messageTopic.split('/');
        const requiredTopic = splitedArr[0]?.concat(`/${splitedArr[1]}`);
        switch (requiredTopic) {
            case 'BSSRM/TREND':
                trendsRef?.current?.addPoints(buildTrendPointFromMqttMessage(message));
                break;
            case 'BSSRM/COMMAND_TOPIC':
                handleGSCommandTopicMessage(message, messageTopic, splitedArr);
                break;
            default:
                break;
        }
    };

    React.useEffect(() => {
        const readyForWidgets = Object.keys(loadingState)?.length === widgets?.length;
        if (mqttConnectionStatus === true && readyForWidgets) {
            subscribeTopic(topics, mqttResponseHandler);
        }

        return (): void => {
            if (topics?.length) {
                unsubscribeTopic(topics);
            }
        };
    }, [mqttConnectionStatus, topics, loadingState, widgets?.length]);

    const renderLoader = (type: any): JSX.Element => {
        if (getWidgetTypeValidationConfig(type)['loaderType'] === 'skeleton') {
            return (
                <DetailsCardSkeleton
                    secondarylength={5}
                    {...(getWidgetTypeValidationConfig(type)?.['primarySkeletonCount'] && {
                        primarylength: getWidgetTypeValidationConfig(type)?.['primarySkeletonCount'],
                    })}
                />
            );
        }
        return <Loader size={55} />;
    };

    const getWidgetLayout = (): any[] => {
        const baseLayout = resolveWidgetLayout(widgets, dashboards);
        if (!baseLayout?.length) return baseLayout;

        return normalizeWidgetGridLayout({
            layout: baseLayout,
            measuredHeights,
            getMinRowsForWidget,
            gridCols: GRID_COLS,
            minW: 14,
        });
    };

    const renderedLayout = getWidgetLayout();

    const getGridContainerMinHeight = (): number => {
        if (!renderedLayout?.length) {
            return GRID_ROWS * 8;
        }

        const maxBottomRow = renderedLayout.reduce(
            (maxBottom: number, item: any) => Math.max(maxBottom, (item?.y ?? 0) + (item?.h ?? 0)),
            0
        );

        return maxBottomRow * GRID_ROWS;
    };

    const pinnedItemHandlers: PinnedItemHandlers = {
        onMapConfig: handleMapConfig,
        scoreModalRef,
        onTrendsPayload: handleTrendsDurationPayload,
        onDownloadTrends: downloadTrends,
        onLegendsPayload: handleLegendsPayload,
        onCommand: handleCommandBarAction,
    };

    const gridItemHandlers: GridItemHandlers = {
        ...pinnedItemHandlers,
        trendsRef,
        onHeightChange: handleWidgetHeightChange,
        onLoads: handleDeviceLoads,
        widgets,
        onWidgetFilter: handleWidgetFilter,
        onUnitFilter: handleUnitTypeFilter,
        onDownloadCsv: handleDownloadCSV,
        onRefreshWeather: handleRefreshWeatherData,
        onLoadGroupToggle: handleLoadGroupToggle,
        loadGroupStates,
        openLoadGroupsModal,
        isWidgetUpdating: isWidgetUpdateLoading,
        onCreateLoadGroup: (): void => setIsCreateLoadGroupModalOpen(true),
    };

    const emptyStateHandlers: EmptyStateHandlers = {
        theme,
        groupId,
        currentOrgId: getCurrentOrgId(currentOrg),
        modalRef,
        canCreateSystemDashboard,
        canCreateUserDashboard,
    };

    const pinnedWidgets = filterPinnedWidgets(widgets);
    const showNoDashboardsState = shouldShowNoDashboardsState(
        dashboards,
        isSuccess,
        isError,
        isLoading,
        isFetching,
        configLoading
    );
    const showNoWidgetsState = shouldShowNoWidgetsState(dashboards, widgets, isLoading, isFetching, configLoading);
    const showAnyLoader = shouldShowAnyLoader(isLoading, isFetching, configLoading);
    const modalGroupIdOrOrg = resolveGroupIdOrOrgId(groupId, currentOrg);
    const loadGroupsModalMaxCount = getLoadGroupsModalMaxCount(widgets, loadGroupsModalState.widgetId);

    const renderPinnedSection = (): JSX.Element => (
        <div>
            {pinnedWidgets.map((widget: any) => (
                <PinnedWidgetItem
                    key={widget?.id}
                    widget={widget}
                    isLoaded={Boolean(loadingState[widget?.id])}
                    renderLoader={renderLoader}
                    handlers={pinnedItemHandlers}
                />
            ))}
        </div>
    );

    const renderGridLayoutSection = (): JSX.Element => (
        <Box
            sx={{ position: 'relative', width: '100%', minHeight: `${getGridContainerMinHeight()}px` }}
            data-testid="all-groups-view"
        >
            {
                // TODO: react-beautiful-dnd package is deprecated and we need to find an alternative package for drag and drop
            }
            {/* @ts-ignore */}
            <ResponsiveReactGridLayout
                className="layout w-100 view-dashboard"
                isResizable={false}
                compactType="vertical"
                containerPadding={[SECONDARY_CARD_GAP_X, SECONDARY_CARD_GAP_Y]}
                margin={SECONDARY_GRID_MARGIN}
                draggableHandle=".drag-handle"
                layout={renderedLayout}
                style={{ position: 'absolute', paddingRight: '10px' }}
                cols={GRID_COLS}
                rowHeight={GRID_ROWS}
            >
                {Boolean(widgets?.length) &&
                    widgets?.map((widget: any) =>
                        widget?.config?.isPinned ? null : (
                            <div key={widget?.id} style={{ zIndex: 999 }}>
                                <GridWidgetContent
                                    widget={widget}
                                    isLoaded={Boolean(loadingState[widget?.id])}
                                    isApplicable={isWidgetApplicable(widget, 'GROUP')}
                                    isConfigured={isWidgetConfigured(widget)}
                                    renderLoader={renderLoader}
                                    handlers={gridItemHandlers}
                                    translate={t}
                                />
                            </div>
                        )
                    )}
            </ResponsiveReactGridLayout>
        </Box>
    );

    const renderMainContent = (): JSX.Element => {
        if (showNoDashboardsState) return <NoDashboardsEmptyState handlers={emptyStateHandlers} />;
        if (showNoWidgetsState) return <NoWidgetsEmptyState />;
        return renderGridLayoutSection();
    };

    const renderGroupSummaryModals = (): JSX.Element => (
        <>
            <CreateDashboardModal ref={modalRef} data-testid="group-summary-create-dashboard-modal" />
            <ScoreModal key="score@121e" ref={scoreModalRef} data-testid="group-summary-score-modal" />
            <SelectLoadGroupsModal
                isVisible={loadGroupsModalState.isVisible}
                isMultiDevice={loadGroupsModalState.isMultiDevice}
                groupId={modalGroupIdOrOrg}
                initialSelectedIds={loadGroupsModalState.initialSelectedIds}
                isUpdating={isWidgetUpdateLoading}
                maxCount={loadGroupsModalMaxCount}
                onCancel={closeLoadGroupsModal}
                onConfirm={(selectedGroups: any[]): void => {
                    void handleLoadGroupsSelect(selectedGroups, loadGroupsModalState.widgetId);
                    closeLoadGroupsModal();
                }}
            />
            <CreateLoadGroupModal
                isOpen={isCreateLoadGroupModalOpen}
                onClose={(): void => setIsCreateLoadGroupModalOpen(false)}
                groupId={modalGroupIdOrOrg}
            />
            <FailedLoadTriggerModal />
        </>
    );

    return (
        <>
            {renderPinnedSection()}
            <Stack>{renderMainContent()}</Stack>
            {showAnyLoader && <Loader size={60} />}
            {renderGroupSummaryModals()}
        </>
    );
};
